// src/components/drivers/DriverDaysWorkedTotals.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Container,
    Card,
    CardBody,
    CardTitle,
    Spinner,
    Row,
    Col,
    Input,
    FormGroup,
    Label,
    Table,
} from "reactstrap";
import Session from '../../auth/Session';

const DriverDaysWorkedTotals = () => {
    // Adjust these to your actual endpoints
    const driversApi = "/api/drivers/daysworked";

    const todayIso = new Date().toISOString().slice(0, 10);
    const sevenDaysAgoIso = (() => {
        const d = new Date();
        d.setDate(d.getDate() - 6);
        return d.toISOString().slice(0, 10);
    })();

    const [dateFrom, setDateFrom] = useState(sevenDaysAgoIso);
    const [dateTo, setDateTo] = useState(todayIso);

    const [drivers, setDrivers] = useState([]);
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [error, setError] = useState("");

    // Load drivers once
    useEffect(() => {
        (async () => {
            setLoadingDrivers(true);
            setError("");
            try {
                const formData = { fromDate: dateFrom, toDate: dateTo };
                const token = await Session.getToken();

                const res = await fetch(driversApi, {
                    method: "POST",
                    headers: token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                });

                if (!res.ok) throw new Error(`Drivers API failed (${res.status})`);
                const data = await res.json();
                setDrivers(data ?? []);
            } catch (e) {
                console.error(e);
                //setError(e?.message ?? "Failed to load drivers");
                setDrivers([]);
            } finally {
                setLoadingDrivers(false);
            }
        })();
    }, []);

    // Load schedules whenever date range changes
    useEffect(() => {
        if (!dateFrom || !dateTo) {
            setDrivers([]);
            return;
        }

        (async () => {
            setLoadingDrivers(true);
            setError("");
            try {
                const formData = { fromDate: dateFrom, toDate: dateTo };
                const token = await Session.getToken();

                const res = await fetch(driversApi, {
                    method: "POST",
                    headers: token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                });

                if (!res.ok) throw new Error(`Schedules API failed (${res.status})`);
                const data = await res.json();
                setDrivers(data ?? []);
            } catch (e) {
                console.error(e);
                //setError(e?.message ?? "Failed to load schedules");
                setDrivers([]);
            } finally {
                setLoadingDrivers(false);
            }
        })();
    }, [dateFrom, dateTo]);

    const formatVal = (val) => {
        if (!val) return "0";
        const fixed = Number(val).toFixed(2);
        return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
    };

    const anyLoading = loadingDrivers;

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5" className="mb-0">
                            Driver Days Worked Totals
                        </CardTitle>
                    </div>

                    <Row className="mb-3">
                        <Col md={4}>
                            <FormGroup>
                                <Label for="dateFrom">From</Label>
                                <Input
                                    id="dateFrom"
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="dateTo">To</Label>
                                <Input
                                    id="dateTo"
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {error ? <div className="text-danger mb-3">{error}</div> : null}

                    {anyLoading ? (
                        <div className="py-5 text-center">
                            <Spinner />
                        </div>
                    ) : !dateFrom || !dateTo ? (
                        <div className="text-muted">Select a date range.</div>
                    ) : new Date(dateFrom) > new Date(dateTo) ? (
                        <div className="text-muted">
                            Invalid range (From must be on/before To).
                        </div>
                    ) : drivers.length === 0 ? (
                        <div className="text-muted">No drivers found.</div>
                    ) : (
                        <Table bordered hover responsive className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Driver</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                        Total Days Worked
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {drivers.map((d) => (
                                    <tr key={d.driverId}>
                                        <td>{d.driverName}</td>
                                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                                            {formatVal(d.daysWorked)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
};

export default DriverDaysWorkedTotals;
